import { createContext, useCallback, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LOCALES, currentLocale, type Locale } from '@/lib/i18n'

interface LocaleContextValue {
  locale: Locale
  setLocale: (l: Locale) => void
  /** The locales offered in the switcher, in display order. */
  available: readonly Locale[]
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

/**
 * Thin wrapper over i18next's own state.
 *
 * i18next already persists the choice and notifies subscribers; this exists so
 * the rest of the app reads the locale the same way it reads the theme, and so
 * `locale` is narrowed to the three we actually ship rather than any string.
 */
export function LocaleProvider({ children }: { children: React.ReactNode }) {
  // Subscribing to `useTranslation` is what makes this provider (and therefore
  // every consumer) re-render when the language changes.
  const { i18n } = useTranslation()

  const setLocale = useCallback(
    (l: Locale) => {
      void i18n.changeLanguage(l)
    },
    [i18n],
  )

  const value = useMemo<LocaleContextValue>(
    () => ({ locale: currentLocale(), setLocale, available: LOCALES }),
    // i18n.resolvedLanguage is the thing that actually changed; depending on it
    // keeps the memo honest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.resolvedLanguage, setLocale],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale() {
  const ctx = useContext(LocaleContext)
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider')
  return ctx
}
